/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FaArrowRight } from "react-icons/fa";

type BlogPost = {
  _id: string;
  heroUrl: string;
  title: string;
  description: string;
  category?: string;
  createdAt?: string;
  showOnHome?: boolean;
};

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const API = process.env.NEXT_PUBLIC_API_URL;

  // ✅ Fetch blogs from backend & filter showOnHome === true
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/blog`);
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          const filtered = data.data.filter((b: any) => b.showOnHome === true);
          setBlogPosts(filtered);
        }
      } catch (err) {
        console.error("Error loading blogs:", err);
      }
    })();
  }, [API]);

  // ✅ Fade-in when section enters view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && setInView(true),
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`w-full bg-white py-14 sm:py-20 px-4 sm:px-6 lg:px-8 transition-all duration-700 ease-out ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-6xl mx-auto text-center">
        {/* --- Header --- */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[80px] judson-regular text-[#000000] mb-4 leading-snug">
          Insights & Guides
        </h2>
        <p className="text-[#938E8E] inter-regular text-sm sm:text-base md:text-lg max-w-3xl mx-auto mb-12 sm:mb-14 leading-relaxed">
          Stay informed with expert advice, market updates, and practical tips to{" "}
          <br className="hidden sm:block" />
          make smarter real estate decisions.
        </p>

        {/* --- Blog Grid --- */}
        <div
          className="
            grid 
            grid-cols-1 
            sm:grid-cols-2 
            lg:grid-cols-3 
            gap-6 sm:gap-30
            justify-center
            p-2
          "
        >
          {blogPosts.map((post) => (
            <Link
              key={post._id}
              href={`/blogs/${post._id}`}
              className="
                group
                border border-gray-200 
                rounded-2xl 
                shadow-sm hover:shadow-lg 
                transition-all duration-300 
                p-4 sm:p-5 
                h-auto
                text-left 
                w-full 
                lg:w-[400px] 
                mx-auto
                block
              "
            >
              {/* --- Image --- */}
              <img
                src={
                  post.heroUrl?.startsWith("http")
                    ? post.heroUrl
                    : "/placeholder.jpg"
                }
                alt={post.title}
                className="
                  w-full 
                  h-44 sm:h-52 md:h-56 lg:h-60 
                  object-cover 
                  rounded-xl 
                  mb-4
                "
              />

              {/* --- Date --- */}
              <p className="text-[#656565] inter-medium text-xs sm:text-sm mb-2">
                {new Date(post.createdAt || "").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>

              {/* --- Title --- */}
              <h3 className="text-lg sm:text-xl md:text-[24px] text-[#212227] inter-medium font-semibold mb-3 leading-snug group-hover:text-[#4177BC] transition-colors duration-300">
                {post.title}
              </h3>

              {/* --- Description --- */}
              <p
                className="text-[#656565] inter-regular text-sm sm:text-base mb-6 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: post.description || "",
                }}
              />

              {/* --- Read More --- */}
              <div className="flex items-center gap-2 inter-regular text-[#262323] group-hover:text-[#4177BC] font-medium group-hover:gap-3 transition-all duration-300">
                <span >Read More</span>
                <FaArrowRight className="text-sm" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
